import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { message } from 'ant-design-vue';
import store from '@/store';
import router from '@/router';
import { RouterEnum } from '@/router/router.enums';
import { CLIENT_APP, withClientHeaders } from '@/utils/clientHeaders';
import { errorCode, standardErrorNotice } from '@/utils/standardErrors';
import {
  geoBlockedNotice,
  isLoginRequest,
  mfaCheckUnavailableNotice,
  retryAfterStepUp,
  withMfaHeader,
} from '@/utils/mfa';
import { requestStepUp } from '@/utils/stepUp';
import { reportGeoBlock } from '@/utils/geoBlockReport';
import {
  LEGACY_BLOCKED_MESSAGES,
  legacyBlockedKind,
} from '@/utils/supportEntry';
import { legacyBlockedState } from '@/utils/legacyBlocked';
import { needsCredentials } from '@/utils/consoleRefresh';
import {
  endSupportTab,
  getAccessToken,
  isSupportEnded,
  isSupportTab,
} from '@/utils/tokenStore';
import { isSupportPagePath, supportEndedUrl } from '@/utils/supportSession';

// 지원 탭이 끝남(401 SUPPORT_SESSION_ENDED): 갱신하지 않고 그 탭을 「끝난 지원 탭」으로 묶은 뒤 끝 화면(계약 SUPPORT 9)
function goSupportEnded(reason?: string) {
  endSupportTab(reason || 'ENDED');
  window.location.replace(supportEndedUrl(reason || 'ENDED'));
}

declare module 'axios' {
  interface AxiosRequestConfig {
    /** false 면 부르는 쪽이 화면 안에서 직접 보이므로 공통 알림을 띄우지 않는다 */
    showErrorNotice?: boolean;
    /** true 면 2차 인증이 필요해도 step-up 창 없이 그대로 실패(사용자가 누르지 않은 조회) */
    skipStepUp?: boolean;
  }
}

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface QueueItem {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  originalRequest: CustomAxiosRequestConfig;
}

const apiInstance = axios.create({
  baseURL: process.env.VUE_APP_SERVERURL,
});

apiInstance.interceptors.request.use(
  (config) => {
    withClientHeaders(config);
    // refresh 쿠키를 심거나 읽는 경로만 쿠키를 주고받는다(K-19)
    if (needsCredentials(config.url)) config.withCredentials = true;
    // 토큰은 tokenStore 한 곳(지원 탭이면 그 탭 sessionStorage). 진입·끝 화면은 토큰을 싣지 않음
    const token = getAccessToken();
    if (token && !isSupportPagePath(window.location.pathname)) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['Access-Control-Allow-Origin'] = '*';
      // 2차 인증 증표(있을 때만). 리셀러도 본인 수단을 켰으면 M·S 경로에 필요(W6 B1)
      withMfaHeader(config);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 권한 이름표(GET /perm-catalog?app=admin-reseller). 403 PERMISSION_DENIED 문구에만 쓰고 한 번만 읽는다(못 받으면 코드 그대로)
let permLabels: Promise<Record<string, string>> | null = null;
const loadPermLabels = () => {
  if (!permLabels) {
    permLabels = apiInstance
      .get('/perm-catalog', {
        params: { app: CLIENT_APP },
        showErrorNotice: false,
      })
      .then(({ data }) =>
        Object.fromEntries(
          (data?.codes || []).map((c: { code: string; ko: string }) => [
            c.code,
            c.ko,
          ])
        )
      )
      .catch(() => ({}));
  }
  return permLabels;
};

let isTokenRefreshing = false;
let failedRequestsQueue: QueueItem[] = [];

apiInstance.interceptors.response.use(
  (response) => {
    store.commit('resetMessageCount');
    return response;
  },

  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;
    const notify = !(
      originalRequest && originalRequest.showErrorNotice === false
    );

    // 지원 탭이 끝났다는 답(401 SUPPORT_SESSION_ENDED {reason}) → 끝 화면(갱신·재전송 없음)
    const data = error.response?.data as
      | { code?: string; reason?: string }
      | undefined;
    if (
      error.response?.status === 401 &&
      data?.code === 'SUPPORT_SESSION_ENDED' &&
      isSupportTab()
    ) {
      goSupportEnded(data.reason);
      return Promise.reject(error);
    }

    // 국외 IP 의 모든 요청(403 GEO_BLOCKED): 안내 문구를 한 번(같은 key) 띄우고 그대로 실패(step-up 없음)
    const geoNotice = geoBlockedNotice(error);
    if (geoNotice) {
      reportGeoBlock(
        isLoginRequest(originalRequest?.url) ? 'LOGIN' : 'REQUEST',
        originalRequest
      );
      if (notify) message.warning({ content: geoNotice, key: 'GEO_BLOCKED' });
      return Promise.reject(error);
    }

    // 2FA 필요 여부 확인 실패(503 MFA_CHECK_UNAVAILABLE): 고정 문구 한 번 뒤 실패 — 재전송·step-up 없음
    const unavailable = mfaCheckUnavailableNotice(error);
    if (unavailable) {
      if (notify)
        message.error({ content: unavailable, key: 'MFA_CHECK_UNAVAILABLE' });
      return Promise.reject(error);
    }

    // 새 오류 코드(권한·이용 내역·지원·계정 생존, W6 E2): 같은 key 로 한 번 띄우고 그대로 실패(재전송·갱신 없음)
    const labels =
      notify && errorCode(error) === 'PERMISSION_DENIED'
        ? await loadPermLabels()
        : {};
    const notice = standardErrorNotice(error, labels);
    if (notice && notice.key === 'LEGACY_ACCESS_BLOCKED') {
      // 옛 대리 길 막힘(W6 B3): 무엇이 막혔는지에 맞는 안내(대리 토큰·음성봇·리셀러 대행 직원 계정)
      const kind = legacyBlockedKind(originalRequest?.url);
      legacyBlockedState.kind = kind;
      notice.text =
        LEGACY_BLOCKED_MESSAGES[kind as keyof typeof LEGACY_BLOCKED_MESSAGES];
    }
    if (notice) {
      if (notify)
        message[notice.level]({ content: notice.text, key: notice.key });
      if (notice.logout) {
        if (isSupportTab()) {
          goSupportEnded('ACCOUNT_INACTIVE');
          return Promise.reject(error);
        }
        store.dispatch('clearLocalStorage');
        router.push(RouterEnum.Signin.path);
      }
      return Promise.reject(error);
    }

    // 2차 인증 필요(MFA_REQUIRED·SEND_AUTH_REQUIRED) → step-up 창 → 통과하면 원래 요청 한 번 재전송
    const stepUp = retryAfterStepUp(error, {
      requestStepUp,
      resend: apiInstance,
    });
    if (stepUp) return stepUp;

    // 끝난 지원 탭: 갱신도 재전송도 하지 않음
    if (isSupportEnded()) return Promise.reject(error);
    // 로그인 단계(비밀번호·twoFactorToken·enrollToken)의 401 은 토큰 만료가 아니므로 갱신·재전송하지 않는다
    if (originalRequest.url && !isLoginRequest(originalRequest.url)) {
      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        if (!isTokenRefreshing) {
          isTokenRefreshing = true;
          await store.dispatch('requestAccessToken');
          isTokenRefreshing = false;

          // 갱신하다 지원이 끝났으면 기다리던 요청까지 모두 실패 — 다른 토큰으로 다시 보내지 않음
          if (isSupportEnded()) {
            failedRequestsQueue.forEach((queueItem) => queueItem.reject(error));
            failedRequestsQueue = [];
            return Promise.reject(error);
          }

          failedRequestsQueue.forEach((queueItem) => {
            queueItem.resolve(apiInstance(queueItem.originalRequest));
          });
          failedRequestsQueue = [];

          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
          return apiInstance(originalRequest);
        } else {
          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({
              resolve,
              reject,
              originalRequest,
            });
          });
        }
      }
    }

    return Promise.reject(error);
  }
);

export default apiInstance;
